/**
 * Componenti presentazionali riutilizzabili.
 *
 * Regola: nessun colore o spaziatura hardcodata — solo classi che consumano i
 * token in styles/tokens.css (ADR-0005). Nessuna logica di dominio qui dentro.
 */

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

// Riesportate da qui perché ogni componente presentazionale si importa da `@/ui`
// e non dal proprio file: l'elenco delle icone disponibili sta in `Icon.tsx`.
export { Icon, IconButton, type IconName } from './Icon';
export { Logo } from './Logo';
export { DomainBadge, DomainBadges } from './DomainBadge';

// ---------------------------------------------------------------- Button

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * `danger` è riservata alle azioni **irreversibili**: quelle che agiscono
   * fuori dall'applicazione — inviare ordini reali (SPEC-014.2 §A.5) — e quelle
   * che distruggono un dato che nessuna sincronizzazione può ricostruire — lo
   * svuotamento del portafoglio collegato (F-113), che cancella gli snapshot
   * giornalieri. Usarla per una conferma qualsiasi la svuoterebbe di
   * significato: «Conferma scollegamento» non è danger, perché i dati restano.
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  block?: boolean;
  loading?: boolean;
};

export function Button({
  variant = 'primary',
  block = false,
  loading = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, block ? 'btn--block' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} disabled={disabled ?? loading} {...rest}>
      {loading && <span className="spinner" aria-hidden="true" style={{ width: 14, height: 14 }} />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------- Card

type CardProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  flush?: boolean;
  children: ReactNode;
};

export function Card({ title, subtitle, action, flush = false, children }: CardProps) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card__header">
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {subtitle && <p className="card__subtitle">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------- StatCard

type StatCardProps = {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'positive' | 'negative';
  /** Un comando legato al numero, in fondo al riquadro (es. «Grafico del rendimento»). */
  action?: ReactNode;
};

export function StatCard({ label, value, hint, tone = 'neutral', action }: StatCardProps) {
  const toneClass =
    tone === 'positive' ? 'text-positive' : tone === 'negative' ? 'text-negative' : '';

  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className={`stat__value ${toneClass}`}>{value}</span>
      {hint && <span className="stat__hint">{hint}</span>}
      {action && <div className="stat__action">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- Field

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /** Allineato a destra, cifre monospaziate, niente freccette dello spinner nativo. */
  numeric?: boolean;
};

export function Field({ label, error, hint, id, numeric = false, ...rest }: FieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={numeric ? 'field__input field__input--numeric' : 'field__input'}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        inputMode={numeric ? 'decimal' : undefined}
        {...rest}
      />
      {error && (
        <span className="field__error" id={`${inputId}-error`} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span className="field__hint" id={`${inputId}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Textarea

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
};

/**
 * Gemello di `Field` per il testo lungo. Condivide le classi `field__*` invece
 * di averne di proprie: sono lo stesso oggetto per l'utente, e due insiemi di
 * stili divergerebbero al primo ritocco.
 */
export function Textarea({ label, error, hint, id, ...rest }: TextareaProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      <textarea
        id={inputId}
        className="field__input field__textarea"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {error && (
        <span className="field__error" id={`${inputId}-error`} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span className="field__hint" id={`${inputId}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Select

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  options: Array<{ value: string; label: string }>;
  /** Voce vuota iniziale, per un campo facoltativo (es. "Nessuno"). */
  placeholder?: string;
};

export function Select({ label, error, hint, id, options, placeholder, ...rest }: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={selectId}>
        {label}
      </label>
      <select
        id={selectId}
        className="field__input"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <span className="field__error" id={`${selectId}-error`} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span className="field__hint" id={`${selectId}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------- SearchableSelect

type SearchableSelectProps = {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean;
  id?: string;
  /** Testo del campo quando non c'è nulla di selezionato. */
  placeholder?: string;
};

/**
 * Una `Select` in cui si può **cercare digitando**.
 *
 * ## Perché non basta `<select>`
 *
 * Il catalogo OpenRouter supera i trecento modelli: una tendina nativa si
 * percorre solo a colpi di prima lettera, e trovare `z-ai/glm-5.2` fra
 * `z-ai/...` significa scorrere a occhio. Il campo dove si sceglie il modello
 * che ragiona su denaro vero non deve essere il posto in cui si sbaglia riga.
 *
 * ## Scritta a mano e non presa da una libreria
 *
 * Una dipendenza per un combobox richiederebbe un ADR (regola del progetto), e
 * porterebbe temi e stili propri da riallineare alle variabili CSS. Il pattern
 * ARIA è piccolo, ed è implementato per intero: `role="combobox"` con
 * `aria-expanded`, `aria-controls`, `aria-activedescendant`, e la lista con
 * `role="listbox"` / `role="option"` e `aria-selected`.
 *
 * ⚠️ **La navigazione da tastiera non è un extra**: senza, il campo sarebbe
 * *peggiore* del `<select>` che sostituisce, dove le frecce funzionano. Frecce
 * per scorrere, Invio per scegliere, Esc per chiudere tornando al valore in
 * vigore — e la voce attiva si mantiene visibile con `scrollIntoView`.
 *
 * ## Il filtro non nasconde il valore scelto
 *
 * Quando si riapre la lista senza digitare, si vedono **tutte** le opzioni: un
 * filtro residuo dalla volta precedente farebbe sembrare sparito un modello che
 * c'è. Il testo digitato vive solo mentre la lista è aperta.
 */
export function SearchableSelect({
  label,
  value,
  options,
  onChange,
  error,
  hint,
  disabled,
  id,
  placeholder,
}: SearchableSelectProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listId = `${fieldId}-list`;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((option) => option.value === value);
  // A lista chiusa il campo mostra l'etichetta del valore in vigore; aperta,
  // mostra ciò che si sta digitando.
  const text = open ? query : (selected?.label ?? '');

  const visible = query.trim()
    ? options.filter((option) =>
        `${option.label} ${option.value}`.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : options;

  // Un clic fuori chiude senza scegliere: la stessa cosa che fa Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // La voce attiva resta visibile anche quando la si raggiunge da tastiera:
  // senza, con trecento opzioni la freccia sposta una selezione invisibile.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-index="${highlighted}"]`);
    // ⚠️ `scrollIntoView` non esiste in jsdom, e un componente non deve
    // rompersi per una funzione che l'ambiente non ha: qui serve a tenere
    // visibile la voce attiva, non a farla funzionare.
    if (node instanceof HTMLElement && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  function choose(index: number) {
    const option = visible[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlighted(Math.max(0, visible.findIndex((option) => option.value === value)));
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((current) => {
        if (visible.length === 0) return 0;
        return (current + step + visible.length) % visible.length;
      });
      return;
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      choose(highlighted);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  }

  return (
    <div className="field" ref={rootRef}>
      <label className="field__label" htmlFor={fieldId}>
        {label}
      </label>
      <div className="combo">
        <input
          id={fieldId}
          className="field__input"
          role="combobox"
          type="text"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && visible[highlighted] ? `${listId}-${highlighted}` : undefined}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlighted(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // ⚠️ Anche sul clic, non solo sul fuoco: dopo Esc il campo resta a
          // fuoco, quindi `onFocus` non si ripete e un clic non riaprirebbe
          // nulla — il campo sembrerebbe bloccato. Trovato da un test.
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {open && (
          <ul className="combo__list" id={listId} role="listbox" ref={listRef}>
            {visible.length === 0 && <li className="combo__empty">Nessun risultato</li>}
            {visible.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={option.value === value}
                className={index === highlighted ? 'combo__option combo__option--on' : 'combo__option'}
                // `onMouseDown` e non `onClick`: il blur dell'input arriverebbe
                // prima e chiuderebbe la lista sotto al puntatore.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(index);
                }}
                onMouseEnter={() => setHighlighted(index)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && (
        <span className="field__error" id={`${fieldId}-error`} role="alert">
          {error}
        </span>
      )}
      {!error && hint && (
        <span className="field__hint" id={`${fieldId}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Alert

type AlertProps = {
  variant?: 'error' | 'warning' | 'info';
  title?: string;
  items?: string[];
  children?: ReactNode;
};

export function Alert({ variant = 'info', title, items, children }: AlertProps) {
  return (
    <div className={`alert alert--${variant}`} role={variant === 'error' ? 'alert' : 'status'}>
      <div>
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
        {items && items.length > 0 && (
          <ul className="alert__list">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Badge

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  /**
   * `warning` non è "negativo attenuato": è per ciò che **deve ancora
   * accadere** e va notato — oggi un innesco programmato. Un evento futuro
   * marcato `accent` si confonderebbe con uno già avvenuto.
   */
  tone?: 'neutral' | 'positive' | 'negative' | 'accent' | 'warning';
  /**
   * Spiegazione al passaggio del mouse, per le etichette che da sole non si
   * spiegano (es. il giudizio `Review` di un'analisi AI). Facoltativa: un
   * badge che dice già tutto non deve guadagnare un tooltip vuoto.
   */
  title?: string;
}) {
  const toneClass = tone === 'neutral' ? '' : `badge--${tone}`;
  return <span className={`badge ${toneClass}`} title={title}>{children}</span>;
}

// ---------------------------------------------------------------- Spinner

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner-wrap">
      <span className="spinner spinner--lg" aria-hidden="true" />
      <span>{label ?? 'Caricamento…'}</span>
    </div>
  );
}

// ---------------------------------------------------------------- EmptyState

type EmptyStateProps = {
  icon?: string;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
};

export function EmptyState({ icon = '📭', title, description, action }: EmptyStateProps) {
  return (
    <div className="empty">
      <span className="empty__icon" aria-hidden="true">
        {icon}
      </span>
      <h3 className="empty__title">{title}</h3>
      {description && <p className="empty__description">{description}</p>}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- Switch

type SwitchProps = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

export function Switch({ label, checked, onChange, disabled = false }: SwitchProps) {
  const id = useId();
  return (
    <div className="switch">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        className={`switch__track ${checked ? 'switch__track--checked' : ''}`}
        onClick={() => !disabled && onChange(!checked)}
      >
        <span className="switch__thumb" />
      </button>
      <label className="switch__label" htmlFor={id}>
        {label}
      </label>
    </div>
  );
}

// ---------------------------------------------------------------- Table

/**
 * `tall` limita l'altezza e fa scrollare il wrapper in verticale: è il
 * prerequisito perché `table--sticky-head` funzioni — con il solo
 * `overflow-x: auto` l'intestazione sticky non avrebbe uno scroller utile.
 */
export function TableWrap({ children, tall = false }: { children: ReactNode; tall?: boolean }) {
  return <div className={tall ? 'table-wrap table-wrap--tall' : 'table-wrap'}>{children}</div>;
}

// ---------------------------------------------------------------- Modal

type ModalProps = {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
};

/**
 * Dialog centrato con sfondo cliccabile: Esc o click fuori chiudono, click dentro no.
 *
 * Renderizzato in un portale su `document.body`, non inline nell'albero React:
 * un Modal aperto da dentro un `<form>` (es. il picker titolo in `TransactionForm`)
 * altrimenti annida un `<form>` dentro un altro `<form>` nel DOM reale — HTML non
 * valido — e il browser lo "corregge" scatenando un submit nativo (refresh di
 * pagina, tutto lo stato React perso) al posto del solo submit interno gestito
 * da React. Il portale evita l'annidamento a monte, qualunque sia il contesto
 * da cui il Modal viene aperto.
 */
export function Modal({ title, onClose, children }: ModalProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="modal__close" aria-label="Chiudi" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
